import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Archive, Check, Copy, ExternalLink, Lock, RefreshCw, ScanLine } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@qrody/ui";
import type { QrCodeResponse } from "@qrody/api-client/codes";
import { CodeThumbnail } from "@/components/CodeThumbnail";
import { useSetAppSidebarSlot } from "@/components/app-sidebar-slot";
import { useTranslations } from "@/i18n/IntlProvider";
import { api } from "@/lib/api";
import { ErrorAlert, type BannerError } from "./ErrorAlert";
import { formatDateTime, scanUrlForSlug } from "./code-utils";

type ScanSummaryResponse = {
  total: number;
  last24h: number;
};

/**
 * Mounts the identity card + lifecycle CTAs + mode toggle into the
 * dashboard sidebar via `useSetAppSidebarSlot`. The hook unmounts the
 * slot on route change, so the sidebar reverts to nav-only as soon as
 * the user leaves /codes/$id.
 *
 * Why this is its own component: react-hooks/rules-of-hooks rejects
 * calling `useSetAppSidebarSlot` from a parent that may early-return
 * for loading / error branches. Lifting the hook call into a sibling
 * that's only rendered when `code` is populated keeps the hook order
 * stable.
 */
export function CodeSidebarPanel({
  code,
  busy,
  errors,
  onLock,
  onConvert,
  onDuplicate,
  onArchive,
}: {
  code: QrCodeResponse;
  busy: {
    lock: boolean;
    convert: boolean;
    duplicate: boolean;
    archive: boolean;
  };
  errors: {
    lock: BannerError | null;
    convert: BannerError | null;
    duplicate: BannerError | null;
    archive: BannerError | null;
  };
  onLock: () => void;
  onConvert: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
}) {
  const t = useTranslations("app.codes.detail");
  const tList = useTranslations("app.codes");
  const tActions = useTranslations("common.codes.actions");

  const isArchived = Boolean(code.archivedAt);
  const isLocked = code.locked;
  const isStatic = code.mode === "STATIC";
  const isDynamic = code.mode === "DYNAMIC";

  // Force <CodeThumbnail> to refetch when payload/design change so a
  // PATCH that mutates the matrix lands in the sidebar immediately.
  const thumbnailCacheKey =
    JSON.stringify(code.payload) +
    JSON.stringify(code.design) +
    String(code.locked) +
    (code.lockedAt ?? "");

  // Scan stats — only meaningful for DYNAMIC codes (STATIC matrices go
  // straight to the phone, no redirect, no counters). Suspense / errors
  // collapse silently to "—" so a flaky Redis / Postgres never makes the
  // sidebar layout jump.
  const scanQuery = useQuery({
    queryKey: ["code", code.id, "scans", "summary"],
    queryFn: () => api<ScanSummaryResponse>(`/v1/codes/${code.id}/scans/summary`),
    enabled: isDynamic,
    retry: false,
    staleTime: 30_000,
  });
  const scanSummary = scanQuery.data ?? null;

  const slot = useMemo<ReactNode>(
    () => (
      <div className="space-y-4">
        <section className="space-y-3 rounded-lg border border-border bg-background p-4">
          <div className="flex items-start gap-3">
            <CodeThumbnail id={code.id} size={64} cacheKey={thumbnailCacheKey} />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                {tList(`mode.${code.mode}`)}
              </span>
              <span
                className={
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                  (isLocked
                    ? "bg-amber-100 text-amber-900"
                    : "bg-emerald-100 text-emerald-900")
                }
              >
                {isLocked ? t("badge.locked") : t("badge.draft")}
              </span>
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                {tList(`status.${code.status}`)}
              </span>
            </div>
          </div>
          <dl className="space-y-2 text-xs">
            <Row label={t("field.type")} value={code.type} />
            <Row
              label={t("field.slug")}
              value={
                code.shortSlug ? (
                  <SlugCell
                    slug={code.shortSlug}
                    copyLabel={t("slug.copy")}
                    copiedLabel={t("slug.copied")}
                    testLabel={t("slug.test")}
                  />
                ) : (
                  "—"
                )
              }
              mono={!code.shortSlug}
            />
            <Row
              label={t("field.id")}
              value={<code className="break-all font-mono text-[11px]">{code.id}</code>}
            />
            <Row label={t("field.created")} value={formatDateTime(code.createdAt)} />
            {code.lockedAt ? (
              <Row label={t("field.lockedAt")} value={formatDateTime(code.lockedAt)} />
            ) : null}
          </dl>
        </section>

        {isDynamic ? (
          <section className="space-y-2 rounded-lg border border-border bg-background p-4">
            <div className="flex items-center gap-2">
              <ScanLine className="size-4 text-muted-foreground" aria-hidden />
              <h2 className="text-sm font-semibold">{t("scans.title")}</h2>
            </div>
            {scanSummary === null ? (
              <p className="text-xs text-muted-foreground">{t("scans.loading")}</p>
            ) : scanSummary.total === 0 ? (
              <p className="text-xs text-muted-foreground">{t("scans.none")}</p>
            ) : (
              <div className="space-y-1">
                <p className="text-2xl font-semibold tabular-nums">
                  {scanSummary.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("scans.total", { count: scanSummary.total.toLocaleString() })}
                  {scanSummary.last24h > 0 ? (
                    <>
                      {" · "}
                      {t("scans.last24h", {
                        count: scanSummary.last24h.toLocaleString(),
                      })}
                    </>
                  ) : null}
                </p>
              </div>
            )}
          </section>
        ) : null}

        <section className="rounded-lg border border-border bg-background p-4">
          <h2 className="text-sm font-semibold">{t("lifecycle.title")}</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {isLocked ? t("lifecycle.descLocked") : t("lifecycle.descDraft")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!isLocked && !isArchived ? (
              <Button
                size="sm"
                onClick={onLock}
                disabled={busy.lock}
                className="gap-1.5"
              >
                <Lock className="size-3.5" aria-hidden />
                {busy.lock ? tList("action.inFlight") : tActions("lock")}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              disabled={busy.duplicate}
              className="gap-1.5"
            >
              <Copy className="size-3.5" aria-hidden />
              {busy.duplicate ? tList("action.inFlight") : tActions("duplicate")}
            </Button>
            {!isArchived ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onArchive}
                disabled={busy.archive}
                className="gap-1.5 text-rose-700 hover:text-rose-800"
              >
                <Archive className="size-3.5" aria-hidden />
                {busy.archive ? tList("action.inFlight") : tActions("archive")}
              </Button>
            ) : null}
          </div>
          {errors.lock ? <ErrorAlert className="mt-2" error={errors.lock} /> : null}
          {errors.duplicate ? (
            <ErrorAlert className="mt-2" error={errors.duplicate} />
          ) : null}
          {errors.archive ? (
            <ErrorAlert className="mt-2" error={errors.archive} />
          ) : null}
        </section>

        {isStatic && !isArchived ? (
          <section className="rounded-lg border border-border bg-background p-4">
            <h2 className="text-sm font-semibold">{t("mode.title")}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("mode.descConvert")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs font-medium">
                {tList("mode.STATIC")}
              </span>
              <span aria-hidden className="self-center text-muted-foreground">→</span>
              <span className="inline-flex items-center rounded-md border border-dashed border-border px-2 py-1 text-xs font-medium text-muted-foreground">
                {tList("mode.DYNAMIC")}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onConvert}
                disabled={busy.convert}
                className="gap-1.5"
              >
                <RefreshCw className="size-3.5" aria-hidden />
                {busy.convert ? tList("action.inFlight") : tActions("convert")}
              </Button>
            </div>
            {errors.convert ? (
              <ErrorAlert className="mt-2" error={errors.convert} />
            ) : null}
          </section>
        ) : null}
      </div>
    ),
    [
      code,
      thumbnailCacheKey,
      isArchived,
      isLocked,
      isStatic,
      isDynamic,
      scanSummary,
      t,
      tList,
      tActions,
      busy.lock,
      busy.convert,
      busy.duplicate,
      busy.archive,
      errors.lock,
      errors.convert,
      errors.duplicate,
      errors.archive,
      onLock,
      onConvert,
      onDuplicate,
      onArchive,
    ],
  );

  useSetAppSidebarSlot(slot);
  return null;
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-1">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>{value}</dd>
    </div>
  );
}

/**
 * Slug display + quick-actions for the sidebar identity card.
 *
 * Renders the slug in monospace, a clipboard-copy button (with a brief
 * "Kopiert"-confirmation), and an external-link button that opens the
 * full scan URL (`{public-site}/r/{slug}`) in a new tab so the user can
 * verify redirect / password / expiry without leaving the editor.
 */
function SlugCell({
  slug,
  copyLabel,
  copiedLabel,
  testLabel,
}: {
  slug: string;
  copyLabel: string;
  copiedLabel: string;
  testLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const url = scanUrlForSlug(slug);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail (insecure context, permission denied).
      // Fall back to a manual selection so the user can still grab the
      // value with Ctrl+C.
      window.prompt(copyLabel, url);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="font-mono text-sm">{slug}</code>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => void onCopy()}
          aria-label={copyLabel}
          title={copied ? copiedLabel : copyLabel}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {copied ? (
            <Check className="size-3.5 text-emerald-600" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={testLabel}
          title={testLabel}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <ExternalLink className="size-3.5" aria-hidden />
        </a>
      </div>
    </div>
  );
}
